import type {
  AttachmentStore,
  CorrectionStanding,
  PharmacyTimeZone,
  QueueStageValue,
  ReviewPath,
  StagePositions,
} from './api.ts'
/**
 * The words on screen, in pt-BR.
 *
 * In one file because the interface must say the same thing everywhere. A
 * disposition rendered "Divergência" in the queue and "Precisa revisar" in the
 * detail is two concepts as far as the person reading it is concerned.
 */

export type Disposition = 'ok' | 'info' | 'divergence' | 'undecided'

export const DISPOSITION_LABEL: Readonly<Record<Disposition, string>> = {
  ok: 'Conferido',
  info: 'Observação',
  divergence: 'Divergência',
  undecided: 'Não avaliado',
}

/**
 * `undecided` is styled distinctly from `divergence`, not merged with it.
 *
 * "Isto diverge" and "não consegui avaliar" ask different things of a
 * pharmacist, and a screen that paints them the same colour is hiding the
 * system's own failures behind the product's findings.
 */
export const DISPOSITION_TONE: Readonly<Record<Disposition, string>> = {
  ok: 'tone-ok',
  info: 'tone-info',
  divergence: 'tone-review',
  undecided: 'tone-undecided',
}

export const CHECK_LABEL: Readonly<Record<string, string>> = {
  patient: 'Paciente',
  prescriber: 'Prescritor',
  // Every council alike -- CRM, CRMV, CRN, CRF -- so not "CRM" (#277).
  registration: 'Registro profissional',
  completeness: 'Completude',
  form: 'Forma farmacêutica',
  quantity: 'Apresentação',
  posology: 'Posologia',
  composition: 'Composição',
  identity: 'Identidade do ativo',
  dose: 'Dose',
  /*
   * Not a comparison, and the label says so.
   *
   * The other nine name what was compared; this one names why it is here at
   * all. Without an entry the badge would print `operational` in English at
   * somebody who reads Portuguese -- the exact failure the note below records.
   */
  operational: 'Regra operacional',
  // The rótulo the ERP will print, held against the receita and the sistema.
  label: 'Rótulo',
}

export const STATUS_LABEL: Readonly<Record<string, string>> = {
  /*
   * Missing entirely, so the badge showed the raw key: an verification sat there
   * saying `waiting` in English at somebody who reads Portuguese. Every status
   * needs a word here -- a label table with a hole in it fails by displaying
   * its own internals.
   */
  waiting: 'Aguardando',
  reading: 'Lendo',
  evaluating: 'Avaliando',
  awaiting_verifier: 'Aguardando conferência',
  awaiting_correction: 'Aguardando correção',
  reverifying: 'Reconferindo',
  verified: 'Conferido',
  // Went to production before anybody here reviewed it. Closed and kept.
  released: 'Produzido sem conferência',
  cancelled: 'Cancelado',
  failed: 'Falhou',
  /*
   * Not a failure, and the word matters.
   *
   * The quotation carried no receita -- only receipts, or nothing that could
   * be opened -- so there was nothing to confer. "Falhou" would send somebody
   * looking for a bug; "Sem receita" says where to look instead, which is the
   * attachments.
   */
  discarded: 'Sem receita',
}

/**
 * The checkpoints a run passes through, in order.
 *
 * Checkpoints only. `waiting` and `awaiting_verifier` used to sit in here as
 * bookends, which is what made the strip draw statuses as steps — and what let
 * the strip's list be mistaken for the list of running statuses, so `evaluating`
 * and `reverifying` silently counted as not running.
 *
 * Mirrors `VERIFICATION_PHASES` in `@rp/core`.
 */
export const STEP_ORDER = [
  'acquiring',
  'classifying',
  'detecting',
  'transcribing',
  // After the reading, because that is where the run reports it: the reading
  // does not need the catalogue, so the freshness check waits until the page
  // has been read. Listed here in the order it is *reached* -- the strip places
  // a step by its index, so a list that disagrees with the workflow draws a run
  // walking backwards.
  'awaiting_catalog',
  'resolving',
  'scoring',
] as const

/**
 * The checkpoints, and nothing else.
 *
 * Six statuses used to be named in here too -- `waiting`, `reverifying`,
 * `evaluating`, `awaiting_verifier`, `verified`, `released` -- left over from
 * when one column held both vocabularies. Each one was a way for the strip to
 * describe a status as though it were a step: a row reading "Aguardando" in the
 * badge and "Na fila" underneath, saying one thing twice in two vocabularies.
 *
 * A step that is not a checkpoint is now a bug rather than something to
 * label, so there is nothing here to hide it.
 */
export const STEP_LABEL: Readonly<Record<string, string>> = {
  acquiring: 'Buscando o orçamento',
  classifying: 'Identificando os anexos',
  awaiting_catalog: 'Atualizando o catálogo dos itens',
  detecting: 'Localizando o texto da receita',
  transcribing: 'Lendo a receita',
  resolving: 'Identificando os ativos',
  scoring: 'Comparando os dois lados',
}

export const VERDICT_LABEL: Readonly<Record<string, string>> = {
  accepted: 'Aceito',
  corrected: 'Corrigido',
  rejected: 'Descartado',
}

/**
 * How we classify a conferente's feedback (#405), in the order the buttons show.
 *
 * Mirrors `TRIAGE_VERDICTS` in `@rp/core`; the values are the wire's.
 */
export const TRIAGE_VERDICT_LABEL: Readonly<Record<string, string>> = {
  agree: 'Concordo',
  disagree: 'Discordo',
}

/** The same two, as the queue's filters name them. */
export const TRIAGE_FILTER_LABEL: Readonly<Record<string, string>> = {
  agree: 'Pertinentes',
  disagree: 'Não pertinentes',
}

/**
 * What each classification counts as, by what the conferente claimed.
 *
 * Mirrors `claimOf` in `@rp/core`: concordo with "não é divergência" is a
 * false positive of ours, concordo with "corrigir" on a row we left quiet is a
 * false negative, and discordo with either is the system having been right.
 * Anything else is pertinent or not without being either.
 */
export const TRIAGE_VERDICT_HINT: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  agree: {
    'flagged-wrongly': 'O conferente tem razão: conta como falso positivo.',
    missed: 'O conferente tem razão: conta como falso negativo.',
    other: 'O feedback é pertinente.',
  },
  disagree: {
    'flagged-wrongly': 'O apontamento estava certo: a IA acertou.',
    missed: 'Não havia divergência a apontar: a IA acertou.',
    other: 'O feedback não é pertinente.',
  },
}

/**
 * Each permission a role can hold (#280), as a pharmacist would say it, in
 * the order the roles screen lists them.
 *
 * Mirrors `Permission` in `@rp/core`; the values are the wire's.
 */
export const PERMISSION_LABEL: Readonly<Record<string, string>> = {
  see_commercial: 'Ver Comercial',
  see_verification: 'Ver Conferência',
  see_production: 'Ver Produção',
  see_analysing: 'Ver em análise',
  see_awaiting_verification: 'Ver aguardando conferência',
  see_awaiting_correction: 'Ver aguardando correção',
  see_concluded: 'Ver concluídas',
  see_closed: 'Ver encerradas',
  see_all_users: 'Ver de todos os usuários',
  act_on_any_stage: 'Agir em qualquer etapa',
  manage_users: 'Gerenciar usuários e funções',
  invite_users: 'Convidar usuários',
}

/**
 * How the roles screen groups the permissions (#439). A role needs at least
 * one stage and one state to see anything: the two are both required.
 */
export enum PermissionKind {
  Stage = 'stage',
  State = 'state',
  Other = 'other',
}

export const PERMISSION_KIND: Readonly<Record<string, PermissionKind>> = {
  see_commercial: PermissionKind.Stage,
  see_verification: PermissionKind.Stage,
  see_production: PermissionKind.Stage,
  see_analysing: PermissionKind.State,
  see_awaiting_verification: PermissionKind.State,
  see_awaiting_correction: PermissionKind.State,
  see_concluded: PermissionKind.State,
  see_closed: PermissionKind.State,
  see_all_users: PermissionKind.Other,
  act_on_any_stage: PermissionKind.Other,
  manage_users: PermissionKind.Other,
  invite_users: PermissionKind.Other,
}

export const PERMISSION_KIND_LABEL: Readonly<Record<PermissionKind, string>> = {
  [PermissionKind.Stage]: 'Etapas',
  [PermissionKind.State]: 'Estados',
  [PermissionKind.Other]: 'Outras',
}

/** What each permission means, under its checkbox. */
export const PERMISSION_HINT: Readonly<Record<string, string>> = {
  see_commercial: 'Conferências cuja requisição ainda está no comercial.',
  see_verification: 'Conferências na bancada de conferência.',
  see_production: 'Conferências que já seguiram para a produção.',
  see_analysing: 'Sendo lidas ou analisadas, a caminho de alguém.',
  see_awaiting_verification: 'Esperando a conferência do farmacêutico.',
  see_awaiting_correction:
    'Mandadas corrigir no sistema e ainda não atualizadas. Quem só vê este grupo vê só o que tem para corrigir.',
  see_concluded: 'Conferidas, ou já liberadas para a produção.',
  see_closed: 'Que pararam sem conferência: falharam, foram canceladas ou descartadas.',
  see_all_users: 'Sem isto, só as conferências dos orçamentos que a própria pessoa digitou.',
  act_on_any_stage: 'Sem isto, uma conferência na Produção é só leitura.',
  manage_users:
    'Dá a cada usuário sua função e usuário no Fórmula Certa, e edita as funções. É o administrador da farmácia.',
  invite_users:
    'Sem gerenciar usuários, só convida com uma função que não tenha mais permissões que a sua.',
}

/**
 * What became of an operational rule on one conferência (#497).
 *
 * Mirrors `RuleOutcome` in `@rp/rules`; the values are the wire's, and a test
 * holds the two lists together.
 */
export enum RuleOutcome {
  Passed = 'passed',
  Fired = 'fired',
  Disabled = 'disabled',
  Failed = 'failed',
}

/**
 * `passed` is "sem apontamento", never "ok" or "conferido": a rule cannot
 * tell having had nothing to check from having checked and found it fine, so
 * the screen claims only that it raised nothing.
 *
 * `fired` is here for completeness. On screen a fired rule is labelled by its
 * severity instead, the way the card above labels it.
 */
export const RULE_OUTCOME_LABEL: Readonly<Record<RuleOutcome, string>> = {
  [RuleOutcome.Passed]: 'sem apontamento',
  [RuleOutcome.Fired]: 'apontou',
  [RuleOutcome.Disabled]: 'desligada',
  [RuleOutcome.Failed]: 'falhou ao rodar',
}

/**
 * What a rule is about, which is where its finding is drawn (#549).
 *
 * Mirrors `RuleSubject` in `@rp/rules`, where each rule declares its own; the
 * values are the wire's, and a test holds the two lists together.
 */
export enum RuleSubject {
  Ingredient = 'ingredient',
  Form = 'form',
  Posology = 'posology',
  Quantity = 'quantity',
  Label = 'label',
  Preparation = 'preparation',
  Requisition = 'requisition',
}

/** How loudly a fired rule speaks. `RuleSeverity` in `@rp/rules`. */
export const RULE_SEVERITY_LABEL: Readonly<Record<'error' | 'reminder', string>> = {
  error: 'divergência',
  reminder: 'lembrete',
}

/** Falls back to the raw key rather than to a blank. */
export function label(map: Readonly<Record<string, string>>, key: string): string {
  return map[key] ?? key
}

/**
 * Statuses in which the analysis is doing something.
 *
 * Mirrors `ANALYSING_STATUSES` in `@rp/core`. The review app is its own build and
 * does not import the server's packages, so the values are the wire's and must
 * match.
 */
const ANALYSING_STATUSES: readonly string[] = ['waiting', 'reading', 'evaluating', 'reverifying']

/**
 * Whether the analysis is doing something right now.
 *
 * One column, one question. This used to weigh a status against a step and
 * decide which was telling the truth — because the two shared a vocabulary and
 * could disagree. They no longer share one: a checkpoint says how far into a
 * run something is, and only a status can say whether a run is happening.
 */
export function isAnalysing(status: string): boolean {
  return ANALYSING_STATUSES.includes(status)
}

/**
 * What each status looks like, as a badge.
 *
 * Coloured by *whose turn it is*, not by severity: what a person scanning the
 * queue wants is "is this mine", and a palette that ranks statuses by how bad
 * they are answers a question nobody asked.
 *
 *   brand   — ours: the analysis is doing something
 *   review  — a pharmacist's turn
 *   warning — somebody else's turn, and we are waiting on them
 *   success — finished
 *   error   — stopped, and not by anybody's choice
 */
export const STATUS_TONE: Readonly<Record<string, { fill: string; text: string; dot: string }>> = {
  /* Ours, and not started: recorded, admitted to nothing, spent nothing. */
  waiting: {
    fill: 'var(--color-fill-weak)',
    text: 'var(--color-text-weak)',
    dot: 'var(--color-text-weak)',
  },
  /*
   * Ours, and running.
   *
   * These three had no entry and fell through to the grey `waiting` default,
   * so a conferência being read looked exactly like one that had not started.
   * They share the brand colour because they are one answer to the only
   * question being asked -- "is this mine yet" -- and the progress line above
   * the badge already says which of the three it is.
   */
  reading: {
    fill: 'var(--color-fill-brand-weak)',
    text: 'var(--color-text-brand)',
    dot: 'var(--color-fill-brand-strong)',
  },
  evaluating: {
    fill: 'var(--color-fill-brand-weak)',
    text: 'var(--color-text-brand)',
    dot: 'var(--color-fill-brand-strong)',
  },
  reverifying: {
    fill: 'var(--color-fill-brand-weak)',
    text: 'var(--color-text-brand)',
    dot: 'var(--color-fill-brand-strong)',
  },
  /* A pharmacist's turn. */
  awaiting_verifier: {
    fill: 'var(--color-fill-error-weak)',
    text: 'var(--color-text-error)',
    dot: 'var(--color-fill-error-strong)',
  },
  /* Comercial's turn, and we are waiting on them. */
  awaiting_correction: {
    fill: 'var(--color-fill-warning-weak)',
    text: 'var(--color-text-warning)',
    dot: 'var(--color-fill-warning-strong)',
  },
  /* Finished. */
  verified: {
    fill: 'var(--color-fill-success-weak)',
    text: 'var(--color-text-success)',
    dot: 'var(--color-fill-success-strong)',
  },
  /* Left the bench without a conferência: closed, quietly. */
  released: {
    fill: 'var(--color-fill-weak)',
    text: 'var(--color-text-weak)',
    dot: 'var(--color-text-weak)',
  },
  /* Stopped by choice, and stopped by failure. */
  cancelled: {
    fill: 'var(--color-fill-weak)',
    text: 'var(--color-text-weak)',
    dot: 'var(--color-text-weak)',
  },
  failed: {
    fill: 'var(--color-fill-error-weak)',
    text: 'var(--color-text-error)',
    dot: 'var(--color-fill-error-strong)',
  },
  /* Nothing to do, rather than something that went wrong. */
  discarded: {
    fill: 'var(--color-fill-weak)',
    text: 'var(--color-text-weak)',
    dot: 'var(--color-text-weak)',
  },
}

/**
 * Every status a person can filter by, in the order they occur.
 *
 * `cancelled` used to be absent, because nothing produced one: the only sender
 * of `cancelVerification` was the stage sweep, and it always passed
 * `outcome: 'released'`, so the filter could only ever return nothing. An
 * orçamento deleted in the ERP now ends here, which is a category somebody has
 * to be able to find -- four of them had to be dug out of the database by hand
 * before it existed.
 */
export const STATUS_ORDER: readonly string[] = [
  'waiting',
  'reading',
  'evaluating',
  'awaiting_verifier',
  'awaiting_correction',
  'reverifying',
  'verified',
  'released',
  'failed',
  'cancelled',
  'discarded',
]

/**
 * The bench stages, mirroring `@rp/core`'s `QueueStage`.
 *
 * Mirrored rather than imported: the review app is its own build and does not
 * depend on the server's packages, which is what lets it be served as static
 * files from anywhere. The values are the wire's, so they must match — the
 * server sends `pos` and `stageOf` partitions it exactly as `stageAt` does.
 */
export const STAGE_LABEL: Readonly<Record<QueueStageValue, string>> = {
  comercial: 'Comercial',
  conferencia: 'Conferência',
  producao: 'Produção',
}

/** Where something happened, as a sentence says it: "concluída no comercial" (#489). */
export const STAGE_WHERE: Readonly<Record<QueueStageValue, string>> = {
  comercial: 'no comercial',
  conferencia: 'na conferência',
  producao: 'na produção',
}

/**
 * Why a status changed, when the status alone does not say. Mirrors
 * `StatusChangeReason` in `@rp/core`; the values are the wire's.
 */
export const STATUS_CHANGE_REASON_LABEL: Readonly<Record<string, string>> = {
  reached_verification_stage: 'Reaberto na conferência',
  requisition_withdrawn: 'Requisição retirada do ERP',
  requisition_reincluded: 'Requisição incluída de novo',
  pote_added_after_read: 'Pote incluído depois da leitura',
  quotation_approved: 'Orçamento aprovado; lido de novo da requisição',
  quotation_expired: 'Orçamento não aprovado a tempo',
  quotation_changed: 'Orçamento alterado depois da leitura',
  attachment_arrived: 'Chegou um anexo; lido de novo',
}

/**
 * `FC12540.POSICAO`, partitioned on where the account's stages start (#673),
 * as `stageAt` in `@rp/core` does: the last stage whose start it has reached,
 * and commercial when none -- position 0, never moved, included. The
 * positions are the session's, so the same position is a different stage at
 * another pharmacy.
 */
export function stageOf(pos: number, positions: StagePositions): QueueStageValue {
  if (pos >= positions.producao) return 'producao'
  if (pos >= positions.conferencia) return 'conferencia'
  return 'comercial'
}

/**
 * How far a conferência got with a person before production (#551).
 * Mirrors `ReviewPath` in `@rp/core`.
 */
export const REVIEW_PATH_LABEL: Readonly<Record<ReviewPath, string>> = {
  unreviewed: 'Sem conferência',
  correction_pending: 'Devendo correção',
  reviewed: 'Conferidas antes',
}

/**
 * What became of a conferência concluded owing a correction (#551), as the
 * end of "N ...". Mirrors `CorrectionStanding` in `@rp/core`.
 */
export const CORRECTION_STANDING_LABEL: Readonly<Record<CorrectionStanding, string>> = {
  resolved: 'conferidas depois da correção',
  waiting: 'aguardando a correção no ERP',
  returned: 'de volta com o conferente',
  released_owing: 'na produção sem a correção conferida',
  closed: 'canceladas ou encerradas',
}

/** Where an account's prescriptions are read from (#639), as its page offers it. */
export const ATTACHMENT_STORE_LABEL: Readonly<Record<AttachmentStore, string>> = {
  erp_database: 'Banco de anexos do ERP',
  google_drive: 'Pasta no Google Drive',
}

/**
 * Brazil's zones (#674), as the account page offers them: the city the tz
 * database names each after, and where it holds. In `PharmacyTimeZone`'s order
 * in `@rp/core`, east to west, which a test holds this to.
 */
export const TIME_ZONE_LABEL: Readonly<Record<PharmacyTimeZone, string>> = {
  'America/Noronha': 'Fernando de Noronha',
  'America/Belem': 'Belém (Pará e Amapá)',
  'America/Fortaleza': 'Fortaleza (MA, PI, CE, RN e PB)',
  'America/Recife': 'Recife (Pernambuco)',
  'America/Araguaina': 'Araguaína (Tocantins)',
  'America/Maceio': 'Maceió (Alagoas e Sergipe)',
  'America/Bahia': 'Salvador (Bahia)',
  'America/Santarem': 'Santarém (oeste do Pará)',
  'America/Sao_Paulo': 'Brasília (São Paulo)',
  'America/Campo_Grande': 'Campo Grande (Mato Grosso do Sul)',
  'America/Cuiaba': 'Cuiabá (Mato Grosso)',
  'America/Porto_Velho': 'Porto Velho (Rondônia)',
  'America/Boa_Vista': 'Boa Vista (Roraima)',
  'America/Manaus': 'Manaus (leste do Amazonas)',
  'America/Eirunepe': 'Eirunepé (oeste do Amazonas)',
  'America/Rio_Branco': 'Rio Branco (Acre)',
}

/**
 * What our tables make of a code the ERP codes check found (#672). Mirrors
 * `ErpCodeVerdict` in `@rp/core`; the values are the wire's, and a test holds
 * the two lists together.
 */
export enum ErpCodeVerdict {
  Read = 'read',
  LeftOut = 'left-out',
  Unknown = 'unknown',
  Blank = 'blank',
}

export const ERP_CODE_VERDICT_LABEL: Readonly<Record<ErpCodeVerdict, string>> = {
  [ErpCodeVerdict.Read]: 'lido',
  [ErpCodeVerdict.LeftOut]: 'ignorado de propósito',
  [ErpCodeVerdict.Unknown]: 'desconhecido',
  [ErpCodeVerdict.Blank]: 'vazio',
}

/**
 * The code columns the ERP codes check counts (#672). Mirrors `ErpCodeColumn`
 * in `@rp/core`; the values are the wire's, and a test holds the two lists
 * together. The ERP's own name for each comes with the result.
 */
export enum ErpCodeColumn {
  Form = 'form',
  PlaceholderUnit = 'placeholder-unit',
  Route = 'route',
  Capsule = 'capsule',
  LineKind = 'line-kind',
  Excipient = 'excipient',
  Association = 'association',
  Council = 'council',
  ControlledList = 'controlled-list',
}

/** What the account page calls each column, and what it is counted over when that is not plain. */
export const ERP_CODE_COLUMN_LABEL: Readonly<
  Record<ErpCodeColumn, { readonly title: string; readonly scope?: string }>
> = {
  [ErpCodeColumn.Form]: { title: 'Forma farmacêutica' },
  [ErpCodeColumn.PlaceholderUnit]: {
    title: 'Unidade, onde ela diz a forma',
    scope: 'Só nos potes cujo código de forma deixa a unidade dizer o que foi feito.',
  },
  [ErpCodeColumn.Route]: { title: 'Via de uso' },
  [ErpCodeColumn.Capsule]: {
    title: 'Tipo de cápsula',
    scope: 'Só nos potes lidos como cápsula: nas outras formas a coluna traz letras antigas.',
  },
  [ErpCodeColumn.LineKind]: { title: 'Tipo de linha' },
  [ErpCodeColumn.Excipient]: { title: 'Excipiente (qsp)' },
  [ErpCodeColumn.Association]: { title: 'Associação' },
  [ErpCodeColumn.Council]: { title: 'Conselho do prescritor' },
  [ErpCodeColumn.ControlledList]: {
    title: 'Lista de controlados',
    scope: 'No catálogo inteiro, sem os produtos excluídos, e não só nas requisições do período.',
  },
}

/**
 * Where an account's ERP connection comes from (#639), as the account page
 * says it. Mirrors `ErpConnectionSource` in `@rp/core`; the keys are the wire's.
 */
export const ERP_CONNECTION_SOURCE_LABEL: Readonly<Record<string, string>> = {
  account: 'Conexão própria desta conta',
  environment: 'Usando as variáveis ERP_* do ambiente, até a conexão ser salva aqui',
  none: 'Sem ERP: nada desta conta lê o Formula Certa',
}
